/**
 * Säljverktyg (Sales Tool) — Super admin page for looking up companies,
 * viewing their Accurator activity, Syna credit data, and platform-wide
 * procurement statistics. Used internally by the sales team.
 */
import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import AppLayout from '../../components/layout/app-layout/app-layout';
import { Card, Text } from '@tremor/react';
import { BarChart } from '../../components/shared/Charts/BarChart/BarChart';
import SummaryBarList from '../../components/shared/Charts/SummaryBarList/SummaryBarList';
import SupplierControls from '../app-add-supplier/supplier-controls';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ReactComponent as InfoIcon } from '../../assets/svg/InfoIconLight.svg';
import Selector from '../../components/shared/Selector/Selector';
import { ServiceSalesTool } from '../../services/service-sales-tool';
import { useTranslation } from 'react-i18next';

import { useHistory } from 'react-router-dom';
import { UNIT } from '../page-dashboard-V2/helper';
import { placeOptions } from '../page-supplier-presentation/PlaceSelector';
import { parseAmount } from '../../util';
import FilteringControl from '../../components/shared/FilteringControl/FilteringControl';
import './page-sales-tool.scss';

type TimeRange = 'today' | 'all' | '12m' | '6m' | '3m' | '1m';

const timeRangeKeys: TimeRange[] = ['today', 'all', '12m', '6m', '3m', '1m'];

const getDateRange = (range: TimeRange) => {
  if (range === 'all' || range === 'today') return {};
  const now = new Date();
  const months = parseInt(range);
  const from = new Date(now);
  from.setMonth(from.getMonth() - months);
  return { fromDate: from.toISOString(), toDate: now.toISOString() };
};

// --- Syna XML response parsers ---
// Syna returns XML converted to JSON with _text/_attributes patterns.
// These parsers extract the relevant fields for display.
const getText = (obj: any) => obj?._text || obj?.text || obj || '';

const parseSynaCompanyData = (data: any) => {
  if (!data?.companyData) return null;
  try {
    const omfragad = data.companyData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return null;
    const addrList = omfragad?.Adresslista?.Adress;
    const addr = Array.isArray(addrList) ? addrList[0] : addrList;
    const bolsate = getText(omfragad?.Bolsate);
    return {
      name: getText(omfragad?.Namnlista?.Namn),
      address: getText(addr?.Gatabox),
      postcode: getText(addr?.Postnr),
      city: getText(addr?.Postort),
      kommun: bolsate ? bolsate.split('/')[0].trim() : '',
      ftgform: getText(omfragad?.Ftgform),
      regdatum: getText(omfragad?.Regdatum),
      orgNumber: omfragad?._attributes?.id || '',
      bransch: (() => {
        const bl = omfragad?.Branschlista?.Bransch;
        if (!bl) return '';
        const first = Array.isArray(bl) ? bl[0] : bl;
        return getText(first?.Branschtext);
      })(),
      board: [] as { name: string; role: string }[], // filled from boardData separately
    };
  } catch {
    return null;
  }
};

const parseSynaRating = (data: any) => {
  if (!data?.ratingData) return null;
  try {
    const omfragad = data.ratingData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return null;
    const klass = omfragad?.Bedomning?.Klassning?.Klass;
    return {
      rating: getText(klass),
      ratingText: klass?._attributes?.not || '',
      limit: getText(omfragad?.Limit),
    };
  } catch {
    return null;
  }
};

const parseSynaBoardData = (data: any): { name: string; role: string }[] => {
  try {
    const omfragad = data?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return [];
    const ledamotList = omfragad?.Ledningslista?.Ledamot;
    if (!ledamotList) return [];
    const list = Array.isArray(ledamotList) ? ledamotList : [ledamotList];
    return list.map((l: any) => {
      const funcs = l?.Funktionslista?.Funktion;
      let role = '';
      if (Array.isArray(funcs)) {
        role = funcs.map((f: any) => f?._attributes?.klartext || '').filter(Boolean).join(', ');
      } else {
        role = funcs?._attributes?.klartext || '';
      }
      // Shorten common role names
      role = role.replace(/Verkställande direktör/gi, 'VD');
      return {
        name: getText(l?.Lednamn) || '',
        role,
      };
    }).filter((m: any) => {
      // Exclude auditors (revisorer) — only show board members and executives
      if (!m.name) return false;
      return !m.role.toLowerCase().includes('revisor');
    });
  } catch {
    return [];
  }
};

interface KPIYear {
  period: string;
  omsattning: number;
  resultat: number;
  balansomslutning: number;
  egetKapital: number;
  soliditet: string;
  anstallda: number;
  kassalikviditet: string;
}

/** Parse Swedish-formatted numbers (e.g. "1 234,56" or "*" for missing data) */
const parseSwedishNumber = (val: string | undefined): number => {
  if (!val || val === '*') return 0;
  return parseAmount(val);
};

const parseSynaKPIs = (data: any): KPIYear[] => {
  try {
    const omfragad = data?.kpiData?.Svar?.Objektlista?.Objekt?.Omfragad;
    if (!omfragad) return [];
    const nyckeltal = omfragad?.Nyckeltalslista?.Nyckeltal;
    if (!nyckeltal) return [];
    const list = Array.isArray(nyckeltal) ? nyckeltal : [nyckeltal];
    return list.map((n: any) => {
      const periodFrom = n?.Period_fr?._text || '';
      const periodTo = n?.Period_to?._text || '';
      const year = periodTo ? `20${periodTo.substring(0, 2)}` : '';
      const month = periodTo ? periodTo.substring(2) : '';
      return {
        period: year && month ? `${year}-${month}` : periodFrom + '-' + periodTo,
        omsattning: parseSwedishNumber(n?.Oms?._text),
        resultat: parseSwedishNumber(n?.Res?._text),
        balansomslutning: parseSwedishNumber(n?.BalOmsl?._text),
        egetKapital: parseSwedishNumber(n?.EgetKap?._text),
        soliditet: n?.Soliditet?._text || '-',
        anstallda: parseSwedishNumber(n?.Anst?._text),
        kassalikviditet: n?.Kassalikv?._text || '-',
      };
    });
  } catch {
    return [];
  }
};

const PageSalesTool: FC = () => {
  const [t] = useTranslation('salesTool');
  const history = useHistory();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [orgOptions, setOrgOptions] = useState<{ label: string; value: string }[]>([]);
  const [, setSearchLoading] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const [supplierStats, setSupplierStats] = useState<any>(null);
  const [platformTotals, setPlatformTotals] = useState<any>(null);
  const [globalAreas, setGlobalAreas] = useState<any[]>([]);
  const [selectedAreaOptions, setSelectedAreaOptions] = useState<any[]>([]);
  const [selectedPlaceOptions, setSelectedPlaceOptions] = useState<any[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [combinedCategoryStats, setCombinedCategoryStats] = useState<any[]>([]);
  const [topCategories, setTopCategories] = useState<any[]>([]);
  const [geographyData, setGeographyData] = useState<any>(null);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [trendYear, setTrendYear] = useState<number>(new Date().getFullYear());
  const [supplierUsers, setSupplierUsers] = useState<any[]>([]);
  const [reachData, setReachData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Selector options
  const areaOptions = useMemo(
    () => globalAreas.map((a: any) => ({ label: a.name, value: a._id })),
    [globalAreas]
  );

  const selectedAreaIds = useMemo(
    () => selectedAreaOptions.map((o: any) => o.value),
    [selectedAreaOptions]
  );
  const selectedPlaces = useMemo(() => {
    const values = selectedPlaceOptions.map((o: any) => o.value);
    // "Hela Sverige" = no geographic filter; empty array tells backend to skip place filtering
    if (values.includes('Hela Sverige')) return [];
    return values;
  }, [selectedPlaceOptions]);

  // Load superadmin global categories and organization list on mount
  useEffect(() => {
    const loadAreas = async () => {
      try {
        const areas = await ServiceSalesTool.getGlobalCategories();
        setGlobalAreas(areas || []);
      } catch (err) {
        console.error('Failed to load global categories:', err);
      }
    };
    const loadOrgs = async () => {
      try {
        const orgs = await ServiceSalesTool.getOrganizations();
        setOrgOptions((orgs || []).map((org: any) => ({
          label: `${org.name} — ${org.organizationNumber || ''}`,
          value: org.organizationNumber || org._id,
        })));
      } catch (err) {
        console.error('Failed to load organizations:', err);
      }
    };
    loadAreas();
    loadOrgs();
    loadPlatformTotals();
    loadGeography();
    loadTopCategories();
  }, []);

  const loadPlatformTotals = async () => {
    try {
      const totals = await ServiceSalesTool.getPlatformTotals();
      setPlatformTotals(totals);
    } catch (err) {
      console.error('Failed to load platform totals:', err);
    }
  };

  const loadGeography = async () => {
    try {
      const geo = await ServiceSalesTool.getGeography();
      setGeographyData(geo);
    } catch (err) {
      console.error('Failed to load geography:', err);
    }
  };

  const loadTopCategories = async () => {
    try {
      const top = await ServiceSalesTool.getTopCategories({ limit: 200 });
      setTopCategories(top);
    } catch (err) {
      console.error('Failed to load top categories:', err);
    }
  };

  // Search handler — accepts query directly to avoid stale closure issues
  const handleSearch = useCallback(async (query?: string) => {
    const q = (query || searchQuery).trim();
    if (!q) return;
    setSearchLoading(true);
    setCompanyData(null);
    setSupplierStats(null);

    try {
      // Syna lookup uses raw query; supplier stats needs normalized org number (no dashes/spaces)
      const [company, stats] = await Promise.all([
        ServiceSalesTool.companyLookup(q).catch(() => null),
        ServiceSalesTool.getSupplierStats(q.replace(/[-\s]/g, '')).catch(() => null),
      ]);
      setCompanyData(company);
      setSupplierStats(stats);

      if (stats?.organizationNumber) {
        loadPrioData(stats.organizationNumber, stats.categories || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const loadPrioData = async (orgNum: string, areaIds: string[]) => {
    try {
      const [users, reach] = await Promise.all([
        ServiceSalesTool.getSupplierUsers(orgNum).catch(() => []),
        ServiceSalesTool.getSupplierReach(orgNum, areaIds).catch(() => null),
      ]);
      setSupplierUsers(users);
      setReachData(reach);
    } catch (err) {
      console.error('Failed to load prio data:', err);
    }
  };

  // Load combined category stats when filters change
  useEffect(() => {
    if (selectedAreaIds.length === 0) {
      setCombinedCategoryStats([]);
      setTrendData([]);
      return;
    }
    setStatsLoading(true);
    const dateRange = getDateRange(timeRange);
    const places = selectedPlaces.length > 0 ? selectedPlaces : undefined;

    Promise.all([
      ServiceSalesTool.getCategoryStatsCombined(selectedAreaIds, {
        places,
        ...dateRange,
        ongoingOnly: timeRange === 'today',
      }),
      ServiceSalesTool.getTrend({
        areaIds: selectedAreaIds,
        period: 'monthly',
        places,
      }),
    ])
      .then(([cats, trend]) => {
        setCombinedCategoryStats(cats);
        setTrendData(trend);
        // Default to latest year with data
        if (trend.length > 0) {
          const maxYear = Math.max(...trend.map((d: any) => d.year));
          setTrendYear(maxYear);
        }
      })
      .catch(err => console.error('Failed to load category stats:', err))
      .finally(() => setStatsLoading(false));
  }, [selectedAreaIds, selectedPlaces, timeRange]);

  // Memoized bid request count map by org name
  const bidRequestMap = useMemo(() => {
    const map = new Map<string, number>();
    (supplierStats?.bidRequestOrgs || []).forEach((org: any) => {
      map.set(org.orgName, org.count);
    });
    return map;
  }, [supplierStats]);

  // Memoized user grouping by email
  const groupedUsers = useMemo(() => {
    if (!supplierUsers || supplierUsers.length === 0) return [];
    const grouped = new Map<string, { name: string; email: string; title: string; phones: string[]; orgs: string[]; totalRequests: number }>();
    for (const u of supplierUsers) {
      const key = (u.email || '').toLowerCase();
      if (!grouped.has(key)) {
        grouped.set(key, { name: u.name, email: u.email, title: u.title || '', phones: [], orgs: [], totalRequests: 0 });
      }
      const g = grouped.get(key)!;
      g.totalRequests += u.requestCount || 0;
      if (u.orgName && !g.orgs.includes(u.orgName)) g.orgs.push(u.orgName);
      if (u.phone && !g.phones.includes(u.phone)) g.phones.push(u.phone);
      if (!g.title && u.title) g.title = u.title;
    }
    return Array.from(grouped.values()).filter(g => g.email && g.name);
  }, [supplierUsers]);

  const companyInfo = useMemo(() => {
    const info = parseSynaCompanyData(companyData);
    if (info && companyData?.boardData) {
      info.board = parseSynaBoardData(companyData.boardData);
    }
    return info;
  }, [companyData]);
  const ratingInfo = useMemo(() => parseSynaRating(companyData), [companyData]);
  const kpiData = useMemo(() => parseSynaKPIs(companyData), [companyData]);

  return (
    <AppLayout title={t('title', 'Säljverktyg')}>
      <div className="sales-tool">
        {/* Search Section — creatable dropdown with all org accounts + free text */}
        <div className="sales-tool__search-section">
          <div className="sales-tool__search-input">
            <Selector
              isCreatable
              isClearable
              isSearchable
              placeholder={t('search.placeholder', 'Sök på organisationsnummer eller företagsnamn...')}
              options={orgOptions}
              value={selectedOrg}
              formatCreateLabel={(input: string) => t('search.formatCreateLabel', 'Sök "{{input}}"...', { input })}
              onChange={(option: any) => {
                setSelectedOrg(option);
                if (!option) {
                  // Cleared
                  setCompanyData(null);
                  setSupplierStats(null);
                  setSearchQuery('');
                  return;
                }
                const q = option.value;
                setSearchQuery(q);
                handleSearch(q);
              }}
            />
          </div>
        </div>

        {/* === Allmän information om Accurator === */}
        <div className="sales-tool__section">
          <h3 className="sales-tool__section-title">{t('section.generalInfo', 'Allmän information om Accurator')}</h3>
          <div className="sales-tool__platform-grid">
            {[
              { label: t('platform.purchasingCustomers.label', 'Inköpskunder (totalt)'), value: platformTotals?.purchasingCustomers || 0,
                tooltip: t('platform.purchasingCustomers.tooltip', 'Antal aktiva organisationer med inköpskonto (activeProducts innehåller PURCHASING).') },
              { label: t('platform.salesCustomers.label', 'Leverantörskunder (totalt)'), value: platformTotals?.salesCustomers || 0,
                tooltip: t('platform.salesCustomers.tooltip', 'Antal aktiva organisationer med säljkonto (activeProducts innehåller SALES).') },
              { label: t('platform.prioSubscriptions.label', 'Prio-medlemskap (aktiva)'), value: platformTotals?.prioSubscriptions || 0,
                tooltip: t('platform.prioSubscriptions.tooltip', 'Antal aktiva Accurator Prio-prenumerationer (produkt RECOMMENDED med giltig slutdatum).') },
              { label: t('platform.publishedSubscriptions.label', 'Publicerade förfrågningar (aktiva abonnemang)'), value: platformTotals?.publishedSubscriptions || 0,
                tooltip: t('platform.publishedSubscriptions.tooltip', 'Antal aktiva prenumerationer på Publicerade förfrågningar (produkt OPEN_QUOTATIONS med giltig slutdatum).') },
              { label: t('platform.ongoingProcurements.label', 'Pågående upphandlingar'), value: platformTotals?.ongoingProcurements || 0,
                tooltip: t('platform.ongoingProcurements.tooltip', 'Antal inköp (areas) i öppna projekt (isOpen: true) där anbudstiden inte gått ut.') },
              { label: t('platform.totalProcurements.label', 'Upphandlingar (totalt)'), value: platformTotals?.totalProcurements || 0,
                tooltip: t('platform.totalProcurements.tooltip', 'Totalt antal inköp (project areas) i aktiva, ej borttagna projekt på hela plattformen.') },
              { label: t('platform.totalVolume.label', 'Total upphandlad volym'), value: platformTotals?.totalVolume || 0, isMSEK: true,
                tooltip: t('platform.totalVolume.tooltip', 'Summan av alla signerade kontraktsvärden (Scrive-kontrakt med signedAt + manuella kontrakt) i aktiva projekt.') },
              { label: t('platform.totalUniqueSuppliers.label', 'Unika leverantörer (totalt)'), value: platformTotals?.totalUniqueSuppliers || 0,
                tooltip: t('platform.totalUniqueSuppliers.tooltip', 'Antal unika organisationsnummer bland alla aktiva leverantörer på plattformen (exkl. superadmin-organisationer).') },
            ].map((item: any) => (
              <TooltipContainer
                key={item.label}
                renderReferenceComponent={(className, ref) => (
                  <div className={`sales-tool__platform-tile ${className}`} ref={ref}>
                    <div className="sales-tool__platform-tile-value">
                      {item.isMSEK ? (
                        <>{Math.round(item.value / 1000000).toLocaleString('sv-SE')} <span className="sales-tool__platform-tile-unit">{t('unit.MSEK', 'MSEK')}</span></>
                      ) : (
                        typeof item.value === 'number' ? item.value.toLocaleString('sv-SE') : item.value
                      )}
                    </div>
                    <div className="sales-tool__platform-tile-label">{item.label}</div>
                  </div>
                )}
              >
                <div style={{ maxWidth: 280, fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>{item.label}</strong>
                  {item.tooltip}
                </div>
              </TooltipContainer>
            ))}
          </div>
        </div>

        {/* Company Info Card */}
        {companyInfo && (
          <div className="sales-tool__company-card">
            <div className="sales-tool__company-card-header">
              <span className="sales-tool__company-card-name">{companyInfo.name}</span>
              <div className="sales-tool__company-card-badges">
                {supplierStats?.accountTypes?.includes('SUPERADMIN') && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--superadmin">{t('badge.superadmin', 'Superadmin')}</span>
                )}
                {supplierStats?.accountTypes?.includes('PURCHASING') && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--purchasing">{t('badge.purchasing', 'Inköp')}</span>
                )}
                {supplierStats?.accountTypes?.includes('SALES') && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--sales">{t('badge.sales', 'Leverantör')}</span>
                )}
                {supplierStats?.subscriptions?.map((sub: any) => (
                  <span key={sub.product} className="sales-tool__company-card-badge sales-tool__company-card-badge--subscription">
                    {t(`subscription.${sub.product}`, sub.product) as string}
                  </span>
                ))}
                {!supplierStats?.accountTypes?.filter((t: string) => t !== 'SUPERADMIN').length &&
                  !supplierStats?.accountTypes?.includes('SUPERADMIN') && supplierStats && (
                  <span className="sales-tool__company-card-badge sales-tool__company-card-badge--none">{t('badge.noAccount', 'Ej konto')}</span>
                )}
              </div>
            </div>
            <div className="sales-tool__company-card-grid">
              <div className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">{t('company.orgNumber', 'Org.nummer')}</div>
                <div className="sales-tool__company-card-field-value">{companyInfo.orgNumber}</div>
              </div>
              <div className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">{t('company.address', 'Adress')}</div>
                <div className="sales-tool__company-card-field-value">
                  {companyInfo.address}, {companyInfo.postcode} {companyInfo.city}
                </div>
              </div>
              <div className="sales-tool__company-card-field">
                <div className="sales-tool__company-card-field-label">{t('company.registrationDate', 'Registreringsdatum')}</div>
                <div className="sales-tool__company-card-field-value">
                  {companyInfo.regdatum?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                </div>
              </div>
              {companyInfo.bransch && (
                <div className="sales-tool__company-card-field">
                  <div className="sales-tool__company-card-field-label">{t('company.industry', 'Bransch')}</div>
                  <div className="sales-tool__company-card-field-value">{companyInfo.bransch}</div>
                </div>
              )}
              {ratingInfo && (
                <div className="sales-tool__company-card-field">
                  <div className="sales-tool__company-card-field-label">{t('company.creditLimit', 'Kreditlimit')}</div>
                  <div className="sales-tool__company-card-field-value">
                    {Number(ratingInfo.limit).toLocaleString('sv-SE')} kr
                  </div>
                </div>
              )}
            </div>

            {/* Board / Ledning & Styrelse */}
            {companyInfo.board?.length > 0 && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">{t('company.board', 'Ledning & Styrelse')}</h4>
                <div className="sales-tool__company-card-grid">
                  {companyInfo.board.map((member: any, i: number) => (
                    <div key={i} className="sales-tool__company-card-field">
                      <div className="sales-tool__company-card-field-label">{member.role}</div>
                      <div className="sales-tool__company-card-field-value">{member.name}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Bokslut / Financial Statements */}
            {kpiData.length > 0 && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">{t('company.financials.title', 'Bokslut')}</h4>
                <div className="sales-tool__bokslut">
                  <div className="sales-tool__bokslut-chart">
                    <BarChart
                      title=""
                      data={kpiData.map(k => ({
                        name: k.period,
                        [t('chart.revenueLabel', 'Omsättning')]: k.omsattning,
                        [t('chart.resultLabel', 'Resultat')]: k.resultat,
                      }))}
                      index="name"
                      categories={[t('chart.revenueLabel', 'Omsättning'), t('chart.resultLabel', 'Resultat')]}
                      colors={['emerald', 'amber']}
                      unit={UNIT.AMOUNT_TKR}
                    />
                  </div>
                  <div className="sales-tool__bokslut-table">
                    <div className="sales-tool__bokslut-header">
                      <span>{t('company.financials.title', 'Bokslut')}</span>
                      <span>{kpiData[0]?.period}</span>
                    </div>
                    {[
                      { label: t('company.financials.revenue', 'Omsättning'), value: kpiData[0]?.omsattning, highlight: true },
                      { label: t('company.financials.result', 'Resultat efter finansnetto'), value: kpiData[0]?.resultat, highlight: true },
                      { label: t('company.financials.totalAssets', 'Summa tillgångar'), value: kpiData[0]?.balansomslutning },
                      { label: t('company.financials.equity', 'Eget kapital'), value: kpiData[0]?.egetKapital, highlight: true },
                      { label: t('company.financials.employees', 'Anställda'), value: kpiData[0]?.anstallda, isCount: true },
                      { label: t('company.financials.solidity', 'Soliditet'), value: kpiData[0]?.soliditet, isSuffix: '%' },
                      { label: t('company.financials.cashLiquidity', 'Kassalikviditet'), value: kpiData[0]?.kassalikviditet, isSuffix: '%' },
                    ].map((row, i) => (
                      <div key={i} className={`sales-tool__bokslut-row ${row.highlight ? 'sales-tool__bokslut-row--highlight' : ''}`}>
                        <span>{row.label}</span>
                        <span>
                          {row.isSuffix
                            ? `${row.value}${row.isSuffix}`
                            : row.isCount
                              ? row.value
                              : `${Number(row.value).toLocaleString('sv-SE')} tkr`}
                        </span>
                      </div>
                    ))}
                    <div className="sales-tool__bokslut-row sales-tool__bokslut-row--footer">
                      <span>{t('company.financials.currencyNote', 'Valutakod (Belopp i 1000)')}</span>
                      <span>SEK</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Supplier Stats inside company card */}
            {supplierStats && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">{t('section.activity', 'Aktivitet på Accurator')}</h4>
                <div className="sales-tool__company-card-stats">
                  {[
                    {
                      value: supplierStats.customerRegisters?.count || 0,
                      label: t('activity.customerRegisters.label', 'Kundregister'),
                      tooltip: t('activity.customerRegisters.tooltip', 'Antal aktiva kundorganisationer (exkl. demo/superadmin) som har en leverantörspost med detta organisationsnummer i sitt register.'),
                    },
                    {
                      value: reachData?.currentCustomers ?? '-',
                      label: t('activity.currentCustomers.label', 'Kunder idag'),
                      tooltip: t('activity.currentCustomers.tooltip', 'Antal unika organisationer som har skickat minst en förfrågan till denna leverantör. Räknas via projektens orgId i aktiva, ej borttagna projekt.'),
                    },
                    {
                      value: supplierStats.bidRequests?.received || 0,
                      label: t('activity.receivedRequests.label', 'Mottagna förfrågningar'),
                      tooltip: t('activity.receivedRequests.tooltip', 'Antal unika inköp (projectId+areaId) där leverantören finns med i suppliers-arrayen i versions-kollektionen. Räknas över alla organisationer.'),
                    },
                    {
                      value: supplierStats.bidRequests?.submitted || 0,
                      label: t('activity.submittedBids.label', 'Inlämnade anbud'),
                      tooltip: t('activity.submittedBids.tooltip', 'Antal unika inköp (projectId+areaId) där leverantören har en supplierBids-post i någon version. Inkluderar bud på borttagna projekt.'),
                    },
                    {
                      value: supplierStats.wonDeals || 0,
                      label: t('activity.wonDeals.label', 'Vunna affärer'),
                      tooltip: t('activity.wonDeals.tooltip', 'Antal inköp där leverantören är tilldelad (reward: true) OCH det finns ett signerat Scrive-kontrakt eller manuellt kontrakt. Enbart aktiva, ej borttagna projekt.'),
                    },
                    {
                      value: groupedUsers.length || 0,
                      label: t('activity.contacts.label', 'Kontaktpersoner'),
                      tooltip: t('activity.contacts.tooltip', 'Antal unika e-postadresser bland aktiva användare (isActive: true) kopplade till leverantörens supplier-poster. Exkl. demo/superadmin-organisationer.'),
                    },
                  ].map(stat => (
                    <TooltipContainer
                      key={stat.label}
                      renderReferenceComponent={(className, ref) => (
                        <div className={`sales-tool__company-card-stat ${className}`} ref={ref}>
                          <span className="sales-tool__company-card-stat-value">{stat.value}</span>
                          <span className="sales-tool__company-card-stat-label">{stat.label}</span>
                        </div>
                      )}
                    >
                      <div style={{ maxWidth: 260, fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                        <strong style={{ display: 'block', marginBottom: 4 }}>{stat.label}</strong>
                        {stat.tooltip}
                      </div>
                    </TooltipContainer>
                  ))}
                </div>
              </>
            )}

            {/* Supplier Controls inside company card */}
            {supplierStats?.organizationNumber && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">{t('section.supplierControls', 'Leverantörskontroller')}</h4>
                <div className="sales-tool__company-card-controls">
                  <SupplierControls
                    organizationNumber={supplierStats.organizationNumber}
                    companyName={supplierStats.name || companyInfo?.name}
                    hasSyna={true}
                    hasCreditsafe={false}
                    hasTic={false}
                    history={history}
                  />
                </div>
              </>
            )}

            {/* Customer Organizations + Users + Bid Request Orgs */}
            {supplierStats && (
              <>
                <div className="sales-tool__company-card-divider" />
                <h4 className="sales-tool__company-card-subtitle">
                  {t('section.orgsWithSupplier', 'Organisationer med leverantören i sitt register ({{count}})', { count: supplierStats.customerRegisters?.count || 0 })}
                </h4>

                <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t('table.organization', 'Organisation')}</th>
                        <th>{t('table.status', 'Status')}</th>
                        <th>{t('table.requestCount', 'Antal förfrågningar')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierStats.customerRegisters?.organizations?.length > 0 ? (
                        supplierStats.customerRegisters.organizations.map((org: any, i: number) => {
                          const status = org.prequalification || 'APPROVED';
                          const label = status === 'APPROVED' ? t('status.approved', 'Godkänd') : status === 'REJECTED' ? t('status.rejected', 'Nekad') : t('status.pending', 'Ansökan');
                          const badgeClass = status === 'APPROVED' ? 'sales-tool__status-badge--approved'
                            : status === 'REJECTED' ? 'sales-tool__status-badge--rejected'
                            : 'sales-tool__status-badge--pending';
                          const bidCount = bidRequestMap.get(org.orgName);
                          return (
                            <tr key={i}>
                              <td>{org.orgName}</td>
                              <td><span className={`sales-tool__status-badge ${badgeClass}`}>{label}</span></td>
                              <td>{bidCount != null ? `${bidCount} ${t('count.suffix', 'st')}` : '–'}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                          {t('empty.noOrgsWithSupplier', 'Inga organisationer har denna leverantör i sitt register.')}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Users as subsection — grouped by email */}
                <h4 className="sales-tool__company-card-subtitle">
                  {t('section.contacts', 'Kontaktpersoner ({{count}})', { count: groupedUsers.length })}
                  <TooltipContainer
                    renderReferenceComponent={(className, ref) => (
                      <span ref={ref} className={className} style={{ marginLeft: 6, cursor: 'help', verticalAlign: 'middle', display: 'inline-flex' }}><InfoIcon /></span>
                    )}
                  >
                    <div style={{ maxWidth: 260, fontSize: 13, padding: '4px 0' }}>
                      {t('contacts.tooltip', 'Kontaktpersoner som finns hos Accurator-användares register')}
                    </div>
                  </TooltipContainer>
                </h4>
                <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t('table.name', 'Namn')}</th>
                        <th>{t('table.email', 'E-post')}</th>
                        <th>{t('table.phone', 'Telefon')}</th>
                        <th>{t('table.title', 'Titel')}</th>
                        <th>{t('table.requests', 'Förfrågningar')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedUsers.length > 0 ? (
                        groupedUsers.map((user, i) => (
                          <TooltipContainer
                            key={i}
                            renderReferenceComponent={(className, ref) => (
                              <tr className={className} ref={ref}>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>
                                  {user.phones[0] || '-'}
                                  {user.phones.length > 1 && (
                                    <TooltipContainer
                                      renderReferenceComponent={(cn, r) => (
                                        <span className={cn} ref={r} style={{ marginLeft: 4, cursor: 'help', verticalAlign: 'middle', display: 'inline-flex' }}>
                                          <InfoIcon />
                                        </span>
                                      )}
                                    >
                                      <div style={{ fontSize: 13, padding: '4px 0' }}>
                                        <strong>{t('contacts.otherPhones', 'Övriga telefonnummer:')}</strong>
                                        {user.phones.slice(1).map((p, j) => <div key={j}>{p}</div>)}
                                      </div>
                                    </TooltipContainer>
                                  )}
                                </td>
                                <td>{user.title || '-'}</td>
                                <td>{user.totalRequests} {t('count.suffix', 'st')}</td>
                              </tr>
                            )}
                          >
                            {user.orgs.length > 0 && (
                              <div style={{ fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                                <strong>{user.orgs.length === 1 ? t('contacts.organization', 'Organisation:') : t('contacts.organizations', 'Organisationer:')}</strong>
                                {user.orgs.map((org, j) => <div key={j}>{org}</div>)}
                              </div>
                            )}
                          </TooltipContainer>
                        ))
                      ) : (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                          {t('empty.noUsers', 'Inga registrerade användare hos leverantören.')}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Show orgs that sent bids but are NOT in the register */}
                {supplierStats.bidRequestOrgs?.some((org: any) =>
                  !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName)
                ) && (
                  <div className="sales-tool__supplier-users" style={{ marginTop: -8 }}>
                    <table>
                      <thead>
                        <tr>
                          <th colSpan={3} style={{ fontWeight: 500, fontSize: 13, color: 'var(--gray-500)', paddingTop: 4 }}>
                            {t('table.otherOrgsWithRequests', 'Övriga organisationer som skickat förfrågningar')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierStats.bidRequestOrgs
                          .filter((org: any) => !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName))
                          .map((org: any, i: number) => (
                            <tr key={i}>
                              <td>{org.orgName}</td>
                              <td>–</td>
                              <td>{org.count} {t('count.suffix', 'st')}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Category & Place Filters with Selector */}
        <div className="sales-tool__section">
          <h3 className="sales-tool__section-title">{t('section.procurementsPerCategory', 'Upphandlingar per kategori')}</h3>

          <div className="selector-row">
            <div>
              <Selector
                isClearable
                isMulti
                isOverflow
                placeholder={t('filter.categories', 'Kategorier')}
                options={areaOptions}
                value={selectedAreaOptions}
                onChange={(e: any) => setSelectedAreaOptions(e || [])}
              />
              {supplierStats?.supplierProfile?.areas?.length > 0 && (() => {
                // Map supplier's org-specific area IDs to global categories via customerAreas
                const supplierGlobalMatches = supplierStats.supplierProfile.areas
                  .map((area: any) => {
                    if (!area?._id) return null;
                    const globalMatch = globalAreas.find((g: any) => g._id === area._id);
                    return globalMatch ? { globalId: globalMatch._id, globalName: globalMatch.name } : null;
                  })
                  .filter(Boolean)
                  // Deduplicate by global ID
                  .filter((v: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.globalId === v.globalId) === i);

                if (!supplierGlobalMatches.length) return null;
                return (
                  <div className="sales-tool__profile-tags">
                    <span className="sales-tool__profile-tags-label">{t('filter.supplierCategories', 'Leverantörens:')}</span>
                    {supplierGlobalMatches.map((match: any) => {
                      const option = areaOptions.find((o: any) => o.value === match.globalId);
                      if (!option) return null;
                      const isSelected = selectedAreaOptions.some((o: any) => o.value === match.globalId);
                      return (
                        <button
                          key={match.globalId}
                          className={`sales-tool__profile-tag ${isSelected ? 'sales-tool__profile-tag--active' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedAreaOptions(selectedAreaOptions.filter((o: any) => o.value !== match.globalId));
                            } else {
                              setSelectedAreaOptions([...selectedAreaOptions, option]);
                            }
                          }}
                        >
                          {match.globalName}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div>
              <Selector
                isClearable
                isMulti
                isOverflow
                placeholder={t('filter.operatingArea', 'Verksamhetsområde')}
                options={placeOptions}
                value={selectedPlaceOptions}
                onChange={(e: any) => setSelectedPlaceOptions(e || [])}
              />
              {supplierStats?.supplierProfile?.places?.length > 0 && (
                <div className="sales-tool__profile-tags">
                  <span className="sales-tool__profile-tags-label">{t('filter.supplierCategories', 'Leverantörens:')}</span>
                  {supplierStats.supplierProfile.places.map((place: string) => {
                    const option = placeOptions.find((o: any) => o.value === place) || { label: place, value: place };
                    const isSelected = selectedPlaceOptions.some((o: any) => o.value === place);
                    return (
                      <button
                        key={place}
                        className={`sales-tool__profile-tag ${isSelected ? 'sales-tool__profile-tag--active' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedPlaceOptions(selectedPlaceOptions.filter((o: any) => o.value !== place));
                          } else {
                            setSelectedPlaceOptions([...selectedPlaceOptions, option]);
                          }
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="sales-tool__filters">
            <div className="sales-tool__filters-group">
              <label>{t('filter.timePeriod', 'Tidsperiod')}</label>
              <FilteringControl
                options={timeRangeKeys.map(key => ({ value: key, label: t(`timeRange.${key}`, key) }))}
                value={timeRange}
                onChange={v => setTimeRange(v as TimeRange)}
              />
            </div>
          </div>

          {/* Combined Category Stats Chart */}
          {statsLoading && <div className="sales-tool__loading">{t('loading.stats', 'Laddar statistik...')}</div>}
          {!statsLoading && selectedAreaIds.length === 0 && (
            <Card style={{ padding: '48px 32px', textAlign: 'center', border: '1px dashed var(--gray-300)', boxShadow: 'none' }}>
              <Text style={{ color: 'var(--gray-400)', fontSize: 14 }}>{t('empty.selectCategory', 'Välj kategori och verksamhetsområde för att visa data')}</Text>
            </Card>
          )}
          {!statsLoading && selectedAreaIds.length > 0 && combinedCategoryStats.length === 0 && (
            <Card style={{ padding: '32px', textAlign: 'center' }}>
              <Text style={{ color: 'var(--gray-500)' }}>{t('empty.noDataForCategory', 'Ingen data för vald kategori')}</Text>
            </Card>
          )}
          {!statsLoading && combinedCategoryStats.length > 0 && (
            <BarChart
              title={t('chart.procurementsPerCategory', 'Upphandlingar per kategori (publicerade vs ej publicerade)')}
              index="areaName"
              data={combinedCategoryStats.map(c => ({
                areaName: c.areaName || t('chart.unknown', 'Okänd'),
                [t('chart.published', 'Publicerade')]: c.publicCount || 0,
                [t('chart.notPublished', 'Ej publicerade')]: c.privateCount || 0,
              }))}
              categories={[t('chart.published', 'Publicerade'), t('chart.notPublished', 'Ej publicerade')]}
              colors={['orange', 'cyan']}
            />
          )}

          {/* Trend — inside Upphandlingar per kategori section */}
          {trendData.length > 0 && (() => {
            const years = Array.from(new Set(trendData.map(d => d.year).filter(Boolean))).sort((a: number, b: number) => b - a);
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
            const filtered = trendData.filter(d => d.year === trendYear);
            return (
              <div style={{ marginTop: 16 }}>
                <div className="sales-tool__section-header" style={{ marginBottom: 8 }}>
                  <div />
                  <FilteringControl
                    options={years.map(y => ({ value: String(y), label: String(y) }))}
                    value={String(trendYear)}
                    onChange={y => setTrendYear(Number(y))}
                  />
                </div>
                <BarChart
                  title={t('chart.procurementsOverTime', 'Upphandlingar över tid (för valda kategorier)')}
                  index="period"
                  data={monthNames.map((name, i) => {
                    const match = filtered.find(d => d.month === i + 1);
                    return { period: name, [t('chart.count', 'Antal')]: match?.count || 0 };
                  })}
                  categories={[t('chart.count', 'Antal')]}
                  colors={['orange']}
                />
              </div>
            );
          })()}
        </div>

        {/* Top Categories & Geography */}
        <div className="sales-tool__section">
          <div className="sales-tool__chart-grid">
            <SummaryBarList
              title={t('chart.topCategories.title', 'Mest efterfrågade kategorier')}
              infoText={t('chart.topCategories.info', 'Visar de kategorier som har flest upphandlingar på plattformen. Kategorier i orange är globala (superadmin), gråa kategorier saknar koppling till en global kategori.')}
              data={topCategories.map(c => {
                const isGlobal = globalAreas.some((g: any) => g._id === c.areaId);
                return {
                  name: c.areaName || t('chart.unknown', 'Okänd'),
                  value: c.count,
                  ...(!isGlobal ? { color: 'gray' as const } : {}),
                };
              })}
              keyColumnName={t('chart.topCategories.keyColumn', 'Kategori')}
              valueColumName={t('chart.topCategories.valueColumn', 'Antal')}
              maxItems={10}
            />
            {geographyData && (
              <SummaryBarList
                title={t('chart.customersByRegion.title', 'Accurator kunder per region')}
                infoText={t('chart.customersByRegion.info', 'Antal kundorganisationer med inköpskonto per region. Baserat på organisationens registrerade kommun.')}
                data={(geographyData.byRegion || []).map((r: any) => ({
                  name: r.region,
                  value: r.count,
                }))}
                keyColumnName={t('chart.customersByRegion.keyColumn', 'Region')}
                valueColumName={t('chart.customersByRegion.valueColumn', 'Antal kunder')}
                maxItems={10}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default PageSalesTool;
